import { IColumn } from "@fluentui/react";
import { debounce } from "cayo.ui";
import { useCallback, useRef } from "react";

const columnsSizesMap = new WeakMap();

type MeasureProcessProps = {
  column: IColumn;
  index: number;
  measureCell: (index: number, onMeasureDone: (width: number) => void) => void;
  onColumnResized: (column: IColumn, width: number, index: number) => void;
};

function measureCellProcess({
  column,
  index,
  measureCell,
  onColumnResized,
}: MeasureProcessProps): Promise<IColumn> {
  return new Promise((resolve) => {
    measureCell(index, (width: number) => {
      if (!columnsSizesMap.has(column)) {
        columnsSizesMap.set(column, width);
      }
      if (columnsSizesMap.get(column) < width) {
        columnsSizesMap.set(column, width);
      }

      onColumnResized(column, columnsSizesMap.get(column), index);
      resolve(column);
    });
  });
}

function parseColumnWidth(
  width: string | number | undefined,
  availableWidth: number,
  starColumnsCount: number,
  remainingWidth: number
): number | null {
  if (width === undefined || width === null) {
    return null;
  }

  const widthStr = String(width).trim();

  // If it's "*" - stretch proportionally
  if (widthStr === "*") {
    return starColumnsCount > 0 ? Math.floor(remainingWidth / starColumnsCount) : null;
  }

  // If it ends with "%" - calculate percentage
  if (widthStr.endsWith("%")) {
    const percent = parseFloat(widthStr.slice(0, -1));
    if (!isNaN(percent)) {
      return Math.floor((availableWidth * percent) / 100);
    }
    return null;
  }

  // If it's a number string - treat as pixels
  const pixelValue = parseFloat(widthStr);
  if (!isNaN(pixelValue)) {
    return pixelValue;
  }

  return null;
}

function calculateColumnWidths(columns: IColumn[], availableWidth: number): Map<number, number> {
  const widthMap = new Map<number, number>();
  let usedWidth = 0;
  const starColumnIndexes: number[] = [];

  // First pass: calculate fixed widths (pixels and percentages)
  columns.forEach((column, index) => {
    const colWidth = (column as any).width;
    if (colWidth === undefined || colWidth === null) {
      return;
    }

    const widthStr = String(colWidth).trim();

    if (widthStr === "*") {
      starColumnIndexes.push(index);
      return;
    }

    const calculatedWidth = parseColumnWidth(widthStr, availableWidth, 0, 0);
    if (calculatedWidth !== null) {
      widthMap.set(index, calculatedWidth);
      usedWidth += calculatedWidth;
    }
  });

  // Second pass: distribute remaining width among star columns
  if (starColumnIndexes.length > 0) {
    const remainingWidth = Math.max(0, availableWidth - usedWidth);
    const starWidth = Math.floor(remainingWidth / starColumnIndexes.length);
    starColumnIndexes.forEach((index) => {
      widthMap.set(index, starWidth);
    });
  }

  return widthMap;
}

export const useAdjustColumns = (columns: IColumn[]) => {
  const ref = useRef(null);

  const adjustColumns = useCallback(
    async (onAdjusted: (columns: IColumn[]) => void, onLoading?: (loading: boolean) => void) => {
      const onColumnResized = (ref?.current as any)?._onColumnResized;
      const batchSize = 20;

      if (onColumnResized) {
        onLoading && onLoading(true);

        // Get available grid width
        const gridWidth = (ref?.current as any)?._root?.current?.offsetWidth || 0;

        // Calculate widths for columns with width property
        const calculatedWidths = calculateColumnWidths(columns, gridWidth);

        // Apply calculated widths to columns
        calculatedWidths.forEach((width, index) => {
          const column = columns[index];
          column.currentWidth = width;
          column.calculatedWidth = width;
          onColumnResized(column, width, index);
        });

        // For columns without width property, measure them as before
        const activeRows = (ref?.current as any)?._activeRows as any[];

        for (let i = 0; i < batchSize; i++) {
          if (Object.prototype.hasOwnProperty.call(activeRows, i)) {
            for (let index = 0; index < columns.length; index++) {
              // Skip columns that already have calculated width
              if (calculatedWidths.has(index)) {
                continue;
              }

              const column = columns[index];
              const currentRow = activeRows[i];
              const measureCell = (index: number, onMeasureDone: (width: number) => void) =>
                currentRow.measureCell(index, onMeasureDone);
              await measureCellProcess({ column, index, measureCell, onColumnResized });
            }
          }
        }

        debounce(
          () => {
            onAdjusted(columns);
            onLoading && onLoading(false);
          },
          onLoading ? 1000 : 0
        )();
      }
    },
    [columns]
  );

  return { adjustColumns, ref };
};
